import React, { useState } from "react";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";

const SWITCHES = ["menu_show_prices", "preorders_enabled"];

/** Switches that come with a feature this shop may not have been given yet. */
const FLAGGED_SWITCHES = [
  { key: "show_sister_shops", flag: "sister_shops" },
  { key: "menu_show_doses", flag: "dose_sales" },
];

/**
 * What the customer menu shows: prices, whether it takes pre-orders, and (once
 * those features reach the shop) its sister shops and which liquids sell by the dose.
 *
 * Each switch saves the moment it is flipped — it is a yes/no about the live
 * menu, not part of a form to fill in. The API enforces both, so turning
 * prices off removes them from what the menu is sent, not just from the page.
 */
const MenuOptions = ({ shop, onSaved }) => {
  const { t } = useI18n();
  const { can, flag } = useMembership();
  const switches = [
    ...SWITCHES,
    ...FLAGGED_SWITCHES.filter((entry) => flag(entry.flag)).map((entry) => entry.key),
  ];
  // Same rule as the opening hours beside it: someone who may only look at
  // the shop gets switches that are shown, not ones that answer with a refusal
  const canEdit = can("shop.edit");
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");

  const flip = async (key) => {
    setSaving(key);
    setError("");

    try {
      const { data } = await shopsAPI.update({ [key]: !shop[key] });
      onSaved(data.shop);
    } catch (err) {
      setError(err.response?.data?.message || t("shop.menuOptions.saveFailed"));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        {t("shop.menuOptions.title")}
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        {canEdit ? t("shop.menuOptions.hint") : t("shop.hours.forbidden")}
      </p>

      {error && <p className="mb-3 text-sm text-red-700">{error}</p>}

      <ul className="divide-y divide-gray-100">
        {switches.map((key) => (
          <li key={key} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <p id={`${key}-label`} className="font-medium text-gray-900">
                {t(`shop.menuOptions.${key}.label`)}
              </p>
              <p className="text-sm text-gray-500">
                {t(`shop.menuOptions.${key}.${shop[key] ? "on" : "off"}`)}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={Boolean(shop[key])}
              aria-labelledby={`${key}-label`}
              disabled={!canEdit || saving !== null}
              onClick={() => flip(key)}
              className={`relative shrink-0 w-12 h-7 rounded-full transition-colors disabled:opacity-60 ${
                shop[key] ? "bg-primary-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${
                  shop[key] ? "start-6" : "start-1"
                }`}
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MenuOptions;
